import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO title="Terms of Service" noindex />
      <Header />
      <main className="container mx-auto px-4 py-20 md:py-28">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4 text-center">
            Terms of Service
          </h1>
          <p className="text-muted-foreground text-center mb-12">
            Effective Date: February 08, 2026
          </p>

          <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">
            <p>
              These Terms of Service govern your purchase and use of Your Moment Song, a service operated by Little Big Moments Co, LLC.
            </p>
            <p>
              By purchasing or accessing a Your Moment Song product, you agree to these Terms of Service. If you do not agree, please do not proceed with your purchase.
            </p>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">1. What Your Moment Song Is</h2>
              <p>Your Moment Song provides custom, one-time digital songs created to celebrate meaningful personal moments such as weddings, anniversaries, birthdays, graduations, memorials, or other special occasions.</p>
              <p>Each song is created based on information you provide and is intended as a private keepsake.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">2. One-Time Purchase</h2>
              <p>All Your Moment Song products are one-off purchases. There is no subscription, recurring billing, or ongoing service unless explicitly stated at the time of purchase.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">3. Personal Use Only</h2>
              <p>All songs are licensed for personal use only.</p>
              <p className="font-semibold">You may:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Listen privately</li>
                <li>Share with friends and family for personal enjoyment</li>
                <li>Gift the song to the intended recipient</li>
              </ul>
              <p className="font-semibold">You may not:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Sell, resell, sublicense, or redistribute the song</li>
                <li>Use the song for commercial, promotional, or business purposes</li>
                <li>Use the song in advertisements, monetized videos, podcasts, or social media</li>
                <li>Perform or broadcast the song publicly</li>
              </ul>
              <p>Commercial or professional use requires prior written permission and a separate licensing agreement.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">4. Ownership and Copyright</h2>
              <p>All songs, music, lyrics, recordings, and related content created through Your Moment Song are copyright protected and owned by Little Big Moments Co, LLC.</p>
              <p>Purchasing a song does not transfer ownership of copyright or intellectual property rights. You receive a limited, non-exclusive, non-transferable license for personal use only.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">5. Privacy and Story Protection</h2>
              <p>Your story matters, and your privacy is important to us.</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The personal details you submit are used only to create your custom song</li>
                <li>Your story, names, and details are not shared, published, or reused</li>
                <li>Your song is not added to any public library or catalog</li>
                <li>Your content is not used for marketing, examples, or promotional materials without your explicit permission</li>
              </ul>
              <p>Your Moment Song creations are treated as private keepsakes.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">6. AI Assisted Creation</h2>
              <p>Some songs are created with the assistance of artificial intelligence as a creative support tool, alongside human direction, review, and curation.</p>
              <p>Regardless of the tools used, all final content is owned by Little Big Moments Co, LLC and protected under copyright law.</p>
              <p>Personal information and story details are not used to train public or third-party AI models.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">7. Accuracy of Information</h2>
              <p>You are responsible for providing accurate names, spellings, dates, and story details.</p>
              <p>We are not responsible for errors resulting from incorrect or unclear information submitted at checkout. Please review all information carefully before final submission.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">8. Digital Delivery</h2>
              <p>All Your Moment Song products are digital and delivered electronically.</p>
              <p>Delivery timelines are provided at the time of purchase. While we strive to meet all timelines, delivery times may vary slightly due to customization requirements.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">9. Refund Policy</h2>
              <p>Due to the custom and personalized nature of Your Moment Song products, all sales are final once production has begun.</p>
              <p>If you experience a technical issue with delivery or playback, please contact us and we will make reasonable efforts to resolve the issue.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">10. Limitation of Liability</h2>
              <p>Your Moment Song is provided on an "as is" basis.</p>
              <p>To the fullest extent permitted by law, Little Big Moments Co, LLC is not liable for indirect, incidental, or consequential damages arising from the use of the song.</p>
              <p>Our total liability, if any, is limited to the amount paid for the product.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">11. Changes to These Terms</h2>
              <p>We may update these Terms of Service from time to time. Updated terms will be posted on our website and apply to future purchases.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">12. Governing Law</h2>
              <p>These Terms of Service are governed by the laws of the state in which Little Big Moments Co, LLC is registered, without regard to conflict of law principles.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">13. Contact Information</h2>
              <p>If you have questions about these Terms of Service, your order, or licensing options, please contact us at:</p>
              <p>
                <Link to="/contact" className="text-primary hover:underline">
                  song@yourmomentsong.com
                </Link>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
